import { useMemo, useState } from "react";
import dayjs from "dayjs";
import {
    Check, ExternalLink, ListTodo, MessageCircle, PartyPopper, Phone, Sparkles, X,
} from "lucide-react";
import Panel, { Empty } from "./Panel";
import { cn } from "../../../utils/tailwindUtils";

const KINDS = {
    overdue:   { label: "Overdue",    cls: "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300" },
    came_back: { label: "Came back",  cls: "bg-fuchsia-50 text-fuchsia-700 dark:bg-fuchsia-900/30 dark:text-fuchsia-300" },
    new:       { label: "New lead",   cls: "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" },
    due_today: { label: "Due today",  cls: "bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300" },
    ai:        { label: "AI suggests", cls: "bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300" },
    stuck:     { label: "Stuck",      cls: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300" },
};
const FILTER_ORDER = ["overdue", "came_back", "new", "due_today", "ai", "stuck"];

// India numbers come in as 10 digits; wa.me wants the country code.
const waNumber = (raw) => {
    const d = String(raw || "").replace(/\D/g, "");
    return d.length === 10 ? `91${d}` : d;
};

const ScoreChip = ({ score }) => {
    if (score === null || score === undefined) return null;
    const tone = score >= 70 ? "text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-300"
        : score >= 40 ? "text-amber-700 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-300"
        : "text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-300";
    return <span className={cn("text-[10px] font-semibold px-1.5 py-0.5 rounded", tone)} title="Lead score">{score}</span>;
};

const IconBtn = ({ title, onClick, href, children, tone }) => {
    const cls = cn(
        "p-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300",
        "hover:bg-gray-50 dark:hover:bg-gray-700 transition", tone,
    );
    return href
        ? <a href={href} title={title} aria-label={title} className={cls} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" onClick={(e) => e.stopPropagation()}>{children}</a>
        : <button type="button" title={title} aria-label={title} className={cls} onClick={(e) => { e.stopPropagation(); onClick(); }}>{children}</button>;
};

const Row = ({ item, onOpen, onSuggestion }) => {
    const kind = KINDS[item.kind] || KINDS.stuck;
    const [showScript, setShowScript] = useState(false);
    return (
        <li
            className="group flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer"
            onClick={() => onOpen(item.customer_id)}
        >
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className={cn("text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded", kind.cls)}>
                        {item.kind === "ai" && item.action_label ? item.action_label : kind.label}
                    </span>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{item.customer_name}</p>
                    <ScoreChip score={item.lead_score} />
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">{item.reasons[0]}</p>
                {item.reasons.length > 1 && (
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 truncate">
                        also: {item.reasons.slice(1).join(" · ")}
                    </p>
                )}
                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                    {item.lead_stage}
                    {item.last_activity_at ? ` · last touch ${dayjs(item.last_activity_at).fromNow()}` : " · never touched"}
                </p>
                {item.suggested_script && showScript && (
                    <p className="mt-2 text-xs text-gray-700 dark:text-gray-200 bg-brand-50/60 dark:bg-brand-900/20 rounded-lg p-2 whitespace-pre-line">
                        {item.suggested_script}
                    </p>
                )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
                {item.contact_number && (
                    <IconBtn title={`Call ${item.contact_number}`} href={`tel:${item.contact_number}`}>
                        <Phone className="h-3.5 w-3.5" />
                    </IconBtn>
                )}
                {item.contact_number && (
                    <IconBtn title="WhatsApp" href={`https://wa.me/${waNumber(item.contact_number)}`} tone="hover:text-emerald-600">
                        <MessageCircle className="h-3.5 w-3.5" />
                    </IconBtn>
                )}
                {item.suggested_script && (
                    <IconBtn title={showScript ? "Hide script" : "Show suggested script"} onClick={() => setShowScript(s => !s)}>
                        <Sparkles className="h-3.5 w-3.5" />
                    </IconBtn>
                )}
                {item.suggestion_id && (
                    <>
                        <IconBtn title="Mark suggestion done" tone="hover:text-emerald-600" onClick={() => onSuggestion(item, "done")}>
                            <Check className="h-3.5 w-3.5" />
                        </IconBtn>
                        <IconBtn title="Dismiss suggestion" tone="hover:text-red-600" onClick={() => onSuggestion(item, "dismissed")}>
                            <X className="h-3.5 w-3.5" />
                        </IconBtn>
                    </>
                )}
                <IconBtn title="Open lead" onClick={() => onOpen(item.customer_id)}>
                    <ExternalLink className="h-3.5 w-3.5" />
                </IconBtn>
            </div>
        </li>
    );
};

// The one list a rep works from: every reason to touch a lead today, ranked by
// the server, one row per lead.
const DoNextQueue = ({ items, total, counts, loading, onOpen, onSuggestion, onViewAll, uncontacted }) => {
    const [filter, setFilter] = useState("");
    const shown = useMemo(
        () => (filter ? (items || []).filter(i => i.kind === filter) : items || []),
        [items, filter],
    );
    const chips = FILTER_ORDER.filter(k => counts?.[k]);

    return (
        <Panel
            title="Do next"
            icon={ListTodo}
            loading={loading}
            action="All follow-ups"
            onAction={onViewAll}
            badge={total > 0 && (
                <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-brand-600 text-white">{total}</span>
            )}
        >
            {chips.length > 1 && (
                <div className="flex gap-1.5 flex-wrap mb-2">
                    <button type="button" onClick={() => setFilter("")}
                        className={cn("text-xs px-2.5 py-1 rounded-full border",
                            !filter ? "bg-brand-600 text-white border-brand-600" : "border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300")}>
                        All
                    </button>
                    {chips.map(k => (
                        <button key={k} type="button" onClick={() => setFilter(f => (f === k ? "" : k))}
                            className={cn("text-xs px-2.5 py-1 rounded-full border",
                                filter === k ? "bg-brand-600 text-white border-brand-600" : "border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300")}>
                            {KINDS[k].label} · {counts[k]}
                        </button>
                    ))}
                </div>
            )}
            {shown.length === 0 ? (
                <Empty
                    icon={PartyPopper}
                    title="Nothing waiting on you right now"
                    hint={uncontacted ? `${uncontacted} new lead(s) still to call` : "Good time to prospect or update your pipeline."}
                />
            ) : (
                <ul className="divide-y divide-gray-100 dark:divide-gray-700/60 -mx-1">
                    {shown.map(item => (
                        <Row key={item.customer_id} item={item} onOpen={onOpen}
                             onSuggestion={onSuggestion} />
                    ))}
                </ul>
            )}
            {total > (items || []).length && !filter && (
                <p className="text-[11px] text-gray-400 text-center mt-2">
                    Showing the top {(items || []).length} of {total}
                </p>
            )}
        </Panel>
    );
};

export default DoNextQueue;
